import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {
  SaveToResultsStepDataName,
  SendToDocumentStepDataName,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {
  getOutputDataReferenceForDataFrame,
  getOutputStepDataForChart,
} from 'analysis-tool/data/getOutputStepDataForDataFrame';
import {UNSET_CONFIG_VARIABLE} from 'analysis-tool/templates/AnalysisTemplateDAO';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible, assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  SAVE_TO_RESULTS_STARTED_EVENT,
  SEND_TO_DOCUMENT_STARTED_EVENT,
} from 'performance/processAnalysesEvents';
import {CreateStepParams, ParentStepReference, StepReference} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {JSONValue} from 'util/Types';

import {getOutputNameFromOutput, isSendToDocumentEnabled} from './OutputsUtil';
import {AnalysisOutput, AnalysisOutputType, ChartOutputConfig} from './Types';

function datasetOutputToStepParam(
  analysisName: string,
  output: AnalysisOutput,
  folderId: string | null
): CreateStepParams {
  assert(output.outputConfig.type === AnalysisOutputType.DATASET, "Output must be of type 'DATASET'");
  const {stepDataName, stepDataIndex: fromStepDataIndex} = getOutputDataReferenceForDataFrame(
    output.outputConfig.step,
    output.outputConfig.dataFrame.id
  )!;

  return {
    stepType: PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
    stepVersion: 0,
    config: {
      folderId: folderId ?? UNSET_CONFIG_VARIABLE,
      datasetName: getOutputNameFromOutput(analysisName, output.outputConfig),
    },
    incomingEdges: [
      {
        fromStepId: output.outputConfig.step.id,
        fromStepDataName: stepDataName,
        fromStepDataIndex: fromStepDataIndex ?? 0,
        toStepDataName: SINGLE_DATASET_INPUT_STEP_DATA_NAME,
        toStepDataIndex: 0,
      },
    ],
    runAfterCreate: true,
  };
}

function resultOutputToStepParam(output: AnalysisOutput): CreateStepParams {
  assert(output.outputConfig.type === AnalysisOutputType.RESULT, "Output must be of type 'RESULT'");
  const dataMapping = output.outputConfig.newDataMapping;
  const targetSpecificMappingState = output.outputConfig.targetSpecificMappingState;
  const {stepDataName, stepDataIndex: fromStepDataIndex} = getOutputDataReferenceForDataFrame(
    output.outputConfig.step,
    output.outputConfig.dataFrame.id
  )!;

  return {
    stepType: PipelineStepType.SAVE_TO_RESULTS,
    stepVersion: 2,
    config: {
      schemaId: dataMapping.resultSchema!.id,
      columnMapping: dataMapping.mappings,
      targetSpecificMapping: targetSpecificMappingState,
    } as unknown as Record<string, JSONValue>,
    incomingEdges: [
      {
        fromStepId: output.outputConfig.step.id,
        fromStepDataName: stepDataName,
        fromStepDataIndex: fromStepDataIndex ?? 0,
        toStepDataName: SaveToResultsStepDataName.INPUT_DATASET,
        toStepDataIndex: 0,
      },
    ],
    runAfterCreate: true,
  };
}

function isSendingToDocument(selectedOutputs: Array<AnalysisOutput>): boolean {
  return isSendToDocumentEnabled() && selectedOutputs.length > 0;
}

interface DocumentStepInputParent {
  [SendToDocumentStepDataName.INPUT_CHARTS]: Array<ParentStepReference>;
  [SendToDocumentStepDataName.INPUT_DATASETS]: Array<ParentStepReference>;
  [SendToDocumentStepDataName.INPUT_SAVED_RESULTS]: Array<ParentStepReference>;
}

function getSendToDocumentStepInputParents(
  newStepParams: Array<CreateStepParams>,
  chartOutputs: Array<AnalysisOutput>
): DocumentStepInputParent {
  const datasetInputs: Array<ParentStepReference> = [];
  const savedToResultsInputs: Array<ParentStepReference> = [];
  const chartInputs: Array<ParentStepReference> = [];
  newStepParams.forEach((stepParam, index) => {
    if (stepParam.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
      datasetInputs.push({
        stepReference: {
          index,
          type: StepReference.INDEX,
        },
        stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
      });
    } else {
      savedToResultsInputs.push({
        stepReference: {
          index,
          type: StepReference.INDEX,
        },
        stepDataName: SaveToResultsStepDataName.SAVED_RESULTS,
      });
    }
  });
  chartOutputs.forEach((output) => {
    const outputStepData = getOutputStepDataForChart(
      output.outputConfig.step,
      (output.outputConfig as ChartOutputConfig).chart.id
    );

    const chartIndexInStep =
      outputStepData?.charts.findIndex(
        (c) =>
          !isInaccessible(c) &&
          assertIsAccessible(c).id === (output.outputConfig as ChartOutputConfig).chart.id
      ) ?? 0;

    chartInputs.push({
      stepReference: {
        stepId: output.outputConfig.step.id,
        type: StepReference.STEP_ID,
      },
      stepDataName: outputStepData!.name,
      fromStepDataIndex: chartIndexInStep,
    });
  });

  return {
    [SendToDocumentStepDataName.INPUT_CHARTS]: chartInputs,
    [SendToDocumentStepDataName.INPUT_DATASETS]: datasetInputs,
    [SendToDocumentStepDataName.INPUT_SAVED_RESULTS]: savedToResultsInputs,
  };
}

function getSendToDocumentStepParams(
  sectionLayout: PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 0>['sectionLayout'],
  documentId: string | null,
  stepInputParents: DocumentStepInputParent,
  lockResultTables: boolean | null
): CreateStepParams {
  const incomingEdges = Object.entries(stepInputParents).flatMap(
    ([toStepDataName, references]: [string, Array<ParentStepReference>]) =>
      references.map((reference, toStepDataIndex) => ({
        fromStepDataName: reference.stepDataName,
        fromStepDataIndex: reference.fromStepDataIndex ?? 0,
        toStepDataName,
        toStepDataIndex,
        ...(reference.stepReference.type === StepReference.STEP_ID
          ? {fromStepId: reference.stepReference.stepId}
          : {fromStepIndex: reference.stepReference.index}),
      }))
  );
  return {
    stepType: PipelineStepType.SEND_TO_DOCUMENT,
    // Stays on v0 even though v1 is now the latest shape: this flow doesn't create plate outputs
    // yet, so there's nothing to migrate. Bumped to v1 when plate support lands.
    stepVersion: 0,
    config: {
      documentId: documentId ?? UNSET_CONFIG_VARIABLE,
      sectionLayout,
      sectionIndex: null,
      lockResultTables,
      hideAnalysisChip: false,
    },
    incomingEdges,
    runAfterCreate: true,
  };
}

export interface CreateOutputParams {
  analysis: PipelineGraphQL;
  documentSectionLayout: PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 0>['sectionLayout'];
  selectedDocumentId: string | null;
  selectedFolderId: string | null;
  selectedOutputs: Array<AnalysisOutput>;
  lockResultTables: boolean | null;
}

export default async function createOutputs({
  analysis,
  documentSectionLayout,
  selectedDocumentId,
  selectedFolderId,
  selectedOutputs,
  lockResultTables,
}: CreateOutputParams): Promise<void> {
  // When adding outputs to a template, `folderId` and `documentId` are not configured.
  const isTemplate = analysis.__typename === 'AnalysisTemplateNode';

  const datasetOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.DATASET
  );
  const resultOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.RESULT
  );
  const chartOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.CHART
  );

  if (isSendingToDocument(selectedOutputs)) {
    logProfilerTimingEvent(SEND_TO_DOCUMENT_STARTED_EVENT);
  }
  if (resultOutputs.length > 0) {
    logProfilerTimingEvent(SAVE_TO_RESULTS_STARTED_EVENT);
  }

  assert(
    selectedFolderId != null || datasetOutputs.length === 0 || isTemplate,
    'selectedFolderId must be defined if there are datasets',
    {
      selectedFolderId,
    }
  );
  const newStepParams: Array<CreateStepParams> = [
    ...datasetOutputs.map((output) => datasetOutputToStepParam(analysis.name, output, selectedFolderId)),
    ...resultOutputs.map((output) => resultOutputToStepParam(output)),
  ];

  const sendingToDocument = isSendingToDocument(selectedOutputs);
  if (sendingToDocument) {
    assert(selectedDocumentId != null || isTemplate, 'selectedDocumentId must be defined');
    const stepInputParents = getSendToDocumentStepInputParents(newStepParams, chartOutputs);

    const sendToDocumentStepParams = getSendToDocumentStepParams(
      documentSectionLayout,
      selectedDocumentId,
      stepInputParents,
      lockResultTables
    );
    newStepParams.push(sendToDocumentStepParams);
  }
  await AnalysisActions.addAndUpdateSteps(analysis.id, {
    stepsToAdd: newStepParams,
    stepsToUpdate: [],
  });
}
